import type { FindingDetailResponse } from '../../lib/api'

/** A table a check put in one evidence value (EvidenceTable.cs): what the finding page draws. */
export interface EvidenceTableData {
  columns: string[]
  rows: string[][]
  note: string | null
}

/**
 * The table in an evidence value, or null when the value is ordinary text.
 *
 * Strict on purpose: only `{"kind":"table", columns: string[], rows: string[][], note?: string}`
 * with every row as wide as the header is a table. Anything else -- including text that merely
 * starts with a brace -- is shown as the text it is, never half-drawn as a broken table.
 */
export function parseEvidenceTable(value: string): EvidenceTableData | null {
  if (!value.startsWith('{')) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(value)
  } catch {
    return null
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const shape = parsed as Record<string, unknown>
  if (shape.kind !== 'table') return null

  const { columns, rows, note } = shape
  if (!Array.isArray(columns) || columns.length === 0) return null
  if (!columns.every((c): c is string => typeof c === 'string')) return null

  if (!Array.isArray(rows)) return null
  if (
    !rows.every(
      (row): row is string[] =>
        Array.isArray(row) && row.length === columns.length && row.every((c) => typeof c === 'string'),
    )
  ) {
    return null
  }

  if (note !== undefined && note !== null && typeof note !== 'string') return null

  return { columns, rows, note: typeof note === 'string' ? note : null }
}

type EvidenceItem = FindingDetailResponse['evidence'][number]

/** Evidence in page order: runs of label/value rows, and tables on their own. */
export type EvidenceBlock =
  | { kind: 'rows'; items: EvidenceItem[] }
  | { kind: 'table'; label: string; table: EvidenceTableData }

export function evidenceBlocks(evidence: EvidenceItem[]): EvidenceBlock[] {
  const blocks: EvidenceBlock[] = []
  for (const item of evidence) {
    const table = parseEvidenceTable(item.value)
    if (table) {
      blocks.push({ kind: 'table', label: item.label, table })
      continue
    }
    const last = blocks[blocks.length - 1]
    if (last?.kind === 'rows') last.items.push(item)
    else blocks.push({ kind: 'rows', items: [item] })
  }
  return blocks
}
